import { FunctionComponent, ReactNode, useState, useContext } from 'react';
import { useFragment, graphql, useRelayEnvironment } from 'react-relay';
import { useDispatch } from 'react-redux';
import { useCheckoutSelector } from '../reducers/root';
import CheckoutConfirmModal from '../shared/CheckoutConfirmModal';
import CheckoutMutationWrapper from '../mutations/CheckoutMutationWrapper';
import { CheckoutServerVarsContext } from '../components/shared/CheckoutServerVarsContext';
import { creditCardDeleted } from '../actions/checkoutPaymentActions';
import { STATES } from '../constants/checkoutConstants';

import { CheckoutPaymentStepCreditCardList_buyer$key } from './__generated__/CheckoutPaymentStepCreditCardList_buyer.graphql';

type PaymentMethod = { __typename: 'CreditCard' | '%other' } | null | undefined;
export function isCreditCard<T extends PaymentMethod>(
    method: T
): method is Extract<T, { __typename: 'CreditCard' }> {
    return method?.__typename === 'CreditCard';
}

type CreditCardRendererOptions = {
    formattedTextMaskedCardNumber: string;
    token: string;
    cardExpiryMonth: string;
    cardExpiryYear: string;
    cardLogo: string;
    cardType: string;
    paymentProcessor: string;
    onDelete: () => void;
};
type CheckoutPaymentStepCreditCardListProps = {
    optionRenderer: (card: CreditCardRendererOptions) => ReactNode;
    buyer: CheckoutPaymentStepCreditCardList_buyer$key;
};

export const CheckoutPaymentStepCreditCardList: FunctionComponent<
    CheckoutPaymentStepCreditCardListProps
> = props => {
    const { optionRenderer } = props;

    const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
    const [cardToDelete, setCardToDelete] = useState<string | null>(null);
    const { isMobile } = useContext(CheckoutServerVarsContext);
    const relayEnvironment = useRelayEnvironment();
    const dispatch = useDispatch();

    const paymentMethodToken = useCheckoutSelector(state => state.transaction.paymentMethodToken);

    const buyer = useFragment(
        graphql`
            fragment CheckoutPaymentStepCreditCardList_buyer on User {
                serviceId
                wallet {
                    paymentMethods {
                        paymentMethod {
                            __typename
                            ... on CreditCard {
                                token
                                cardLogo
                                cardType
                                cardExpiryMonth
                                cardExpiryYear
                                formattedTextMaskedCardNumber
                                hashedCardNumber
                                paymentProcessor
                            }
                        }
                    }
                }
            }
        `,
        props.buyer
    );

    const creditCards =
        buyer?.wallet?.paymentMethods?.map(method => method?.paymentMethod).filter(isCreditCard) ||
        [];

    function handleDelete(token: string): void {
        setIsConfirmDeleteModalOpen(true);
        setCardToDelete(token);
    }

    async function handleConfirmDelete(): Promise<void> {
        const buyerId = buyer?.serviceId;
        const paymentMethod = creditCards.find(card => card.token === cardToDelete);

        if (!buyerId || !paymentMethod) {
            setIsConfirmDeleteModalOpen(false);
            return;
        }

        try {
            const response = await CheckoutMutationWrapper.commit(
                'UserDeletePaymentMethodMutation',
                relayEnvironment,
                {
                    userId: buyer?.serviceId,
                    paymentMethodType: 'CREDIT_CARD',
                    paymentMethodKey: paymentMethod?.hashedCardNumber,
                },
                isMobile
            );
            // @ts-ignore
            const user = response?.userDeletePaymentMethod?.user;
            const hasCreditCards = user?.wallet?.hasCreditCards;
            const isSelectedCardDeleted = paymentMethodToken === cardToDelete;

            dispatch(
                creditCardDeleted({
                    creditCardState: hasCreditCards ? STATES.STATE_SAVED : STATES.STATE_NEW,
                    paymentMethodToken: isSelectedCardDeleted
                        ? user?.firstValidPaymentMethod?.token
                        : paymentMethodToken,
                    isSelectedCardDeleted,
                })
            );
            setIsConfirmDeleteModalOpen(false);
            setCardToDelete(null);
        } catch (error) {
            setIsConfirmDeleteModalOpen(false);
            setCardToDelete(null);
        }
    }

    function handleConfirmCancel(): void {
        setIsConfirmDeleteModalOpen(false);
        setCardToDelete(null);
    }

    return (
        <>
            {creditCards.map(card =>
                optionRenderer({
                    token: card?.token || '',
                    paymentProcessor: card?.paymentProcessor || '',
                    formattedTextMaskedCardNumber: card?.formattedTextMaskedCardNumber || '',
                    cardExpiryMonth: card?.cardExpiryMonth || '',
                    cardExpiryYear: card?.cardExpiryYear || '',
                    cardLogo: card?.cardLogo || '',
                    cardType: card?.cardType || '',
                    onDelete: () => handleDelete(card?.token || ''),
                })
            )}

            <CheckoutConfirmModal
                isOpen={isConfirmDeleteModalOpen}
                onDischarge={handleConfirmCancel}
                onConfirm={handleConfirmDelete}
                onClose={handleConfirmCancel}
            />
        </>
    );
};
